import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import NavigationBar from './components/NavigationBar';
import UserProfileModal from './components/UserProfileModal';
import HomePage from './pages/HomePage';
import ChampionshipsPage from './pages/ChampionshipsPage';
import ChampionshipDetailsPage from './pages/ChampionshipDetailsPage';
import DriversPage from './pages/DriversPage';
import DriverDetailPage from './pages/DriverDetailPage';
import HotlapPage from './pages/HotlapPage';
import { Championship, UserProfile } from './types';
import api, { getApiBaseUrl } from './api/client';
import { getDisplayTimezone } from './utils/time';
import './App.css';

// Wrapper component to track active page from route
const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getActivePage = () => {
    if (location.pathname === '/') return 'home';
    if (location.pathname.startsWith('/championships')) return 'championships';
    if (location.pathname.startsWith('/drivers')) return 'drivers';
    if (location.pathname.startsWith('/hotlap')) return 'hotlap';
    return 'home';
  };

  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('ace-results-theme') === 'light' ? 'light' : 'dark';
  });
  const [logoLightUrl, setLogoLightUrl] = useState<string | null>(null);
  const [logoDarkUrl, setLogoDarkUrl] = useState<string | null>(null);
  const [broadcastPlatform, setBroadcastPlatform] = useState<'youtube' | 'twitch' | ''>('');
  const [broadcastChannel, setBroadcastChannel] = useState('');
  const [broadcastChannelId, setBroadcastChannelId] = useState('');
  const [defaultRulebookType, setDefaultRulebookType] = useState<'text' | 'link' | 'pdf'>('text');
  const [defaultRulebook, setDefaultRulebook] = useState('');
  const [defaultRulebookPdfDataUrl, setDefaultRulebookPdfDataUrl] = useState<string | null>(null);
  const [pageVisibility, setPageVisibility] = useState({ home: true, championships: true, drivers: true, hotlaps: true });
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [displayTimezone, setDisplayTimezone] = useState<string>(getDisplayTimezone);
  const [user, setUser] = useState<UserProfile | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage.getItem('ace-public-user-profile');
      return stored ? JSON.parse(stored) as UserProfile : null;
    } catch {
      return null;
    }
  });
  const [authToken, setAuthToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('ace-public-auth-token');
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalMode, setProfileModalMode] = useState<'login' | 'profile' | 'settings'>('login');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
    window.localStorage.setItem('ace-results-theme', theme);
  }, [theme]);

  useEffect(() => {
    const isTouchEnabled = () => {
      return (
        window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
        ('ontouchstart' in window) ||
        // @ts-ignore
        navigator.maxTouchPoints > 0
      );
    };
    setIsTouchDevice(isTouchEnabled());
  }, []);

  const dismissToast = () => {
    if (toastTimer.current) {
      window.clearTimeout(toastTimer.current);
      toastTimer.current = null;
    }
    setToast(null);
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    if (toastTimer.current) {
      window.clearTimeout(toastTimer.current);
    }
    toastTimer.current = window.setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        window.clearTimeout(toastTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const callbackToken = params.get('token');
    const authError = params.get('authError');
    const steamLinked = params.get('steamLinked');

    if (callbackToken) {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete('token');
      nextUrl.searchParams.delete('authError');
      nextUrl.searchParams.delete('steamLinked');
      window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      persistAuthState(null, callbackToken);
      showToast('success', 'Signed in with Discord.');
      return;
    }

    if (authError === 'discord') {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete('authError');
      nextUrl.searchParams.delete('steamLinked');
      window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      showToast('error', 'Discord sign-in failed.');
      return;
    }

    if (authError === 'steam' || steamLinked === '1') {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete('authError');
      nextUrl.searchParams.delete('steamLinked');
      window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
      showToast(steamLinked === '1' ? 'success' : 'error', steamLinked === '1' ? 'Steam linked successfully.' : 'Steam linking failed.');
    }
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      if (!authToken) return;
      try {
        const res = await api.getCurrentUser(authToken);
        const nextUser = res.data.user as UserProfile;
        setUser(nextUser);
        window.localStorage.setItem('ace-public-user-profile', JSON.stringify(nextUser));
      } catch (err) {
        console.warn('Unable to restore public profile session', err);
        setUser(null);
        setAuthToken(null);
        window.localStorage.removeItem('ace-public-user-profile');
        window.localStorage.removeItem('ace-public-auth-token');
      }
    };

    restoreSession();
  }, [authToken]);

  useEffect(() => {
    // Try fetching persisted config from backend. Fall back to localStorage.
    const applyConfig = (p: any) => {
      const set = (k: string, v: string) => document.documentElement.style.setProperty(k, v);
      if (p.secondaryColor) { set('--secondary-color', p.secondaryColor); set('--button-bg', p.secondaryColor); set('--button-border', p.secondaryColor); }
      if (p.accentStart) set('--accent-start', p.accentStart);
      if (p.accentEnd) set('--accent-end', p.accentEnd);
      if (p.progressStart) set('--progress-start', p.progressStart);
      if (p.progressEnd) set('--progress-end', p.progressEnd);
      if (p.faviconDataUrl) {
        let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = p.faviconDataUrl;
      }
      if (p.broadcastPlatform) setBroadcastPlatform(p.broadcastPlatform as 'youtube' | 'twitch' | '');
      if (p.broadcastChannel) setBroadcastChannel(p.broadcastChannel);
      if (p.broadcastChannelId) setBroadcastChannelId(p.broadcastChannelId);
      if (p.rulebookType) setDefaultRulebookType(p.rulebookType as 'text' | 'link' | 'pdf');
      if (p.rulebook !== undefined) setDefaultRulebook(p.rulebook);
      if (p.rulebookPdfDataUrl) setDefaultRulebookPdfDataUrl(p.rulebookPdfDataUrl);
      if (p.pageVisibility && typeof p.pageVisibility === 'object') {
        setPageVisibility(prev => ({ ...prev, ...p.pageVisibility }));
      }
    };

    const load = async () => {
      try {
        const resp = await fetch(`${getApiBaseUrl()}/website-config`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const body = await resp.json();
        const parsed = body?.config || {};
        if (parsed.logoLightDataUrl) setLogoLightUrl(parsed.logoLightDataUrl);
        if (parsed.logoDarkDataUrl) setLogoDarkUrl(parsed.logoDarkDataUrl);
        applyConfig(parsed);
        return;
      } catch (err) {
        // fallback to localStorage
      }

      try {
        const savedConfig = window.localStorage.getItem('websiteConfigData');
        if (!savedConfig) return;
        const parsed = JSON.parse(savedConfig) as Record<string, any>;

        if (parsed.logoLightDataUrl) setLogoLightUrl(parsed.logoLightDataUrl);
        if (parsed.logoDarkDataUrl) setLogoDarkUrl(parsed.logoDarkDataUrl);
        applyConfig(parsed);
      } catch (err) {
        console.warn('Unable to load website config from storage', err);
      }
    };

    load();
  }, []);


  const persistAuthState = (nextUser: UserProfile | null, nextToken: string | null) => {
    setUser(nextUser);
    setAuthToken(nextToken);

    if (nextUser) {
      window.localStorage.setItem('ace-public-user-profile', JSON.stringify(nextUser));
    } else {
      window.localStorage.removeItem('ace-public-user-profile');
    }

    if (nextToken) {
      window.localStorage.setItem('ace-public-auth-token', nextToken);
    } else {
      window.localStorage.removeItem('ace-public-auth-token');
    }
  };

  const handleOpenProfileModal = (mode: 'profile' | 'settings') => {
    setProfileModalMode(mode);
    setProfileModalOpen(true);
  };

  const handleOpenUserProfile = async () => {
    if (!user?.steamId) {
      setProfileModalMode('settings');
      setProfileModalOpen(true);
      showToast('error', 'Link your Steam account first.');
      return;
    }

    try {
      const res = await api.getDrivers(user.steamId.trim());
      const match = res.data.drivers?.find((driver: { playerId?: string; steamId?: string; driverId?: string; id?: number }) =>
        driver.playerId === user.steamId?.trim() || driver.steamId === user.steamId?.trim()
      );
      if (match?.id) {
        navigate(`/drivers/${match.id}`);
        return;
      } else if (match?.driverId) {
        // Fallback to legacy driverId
        navigate(`/drivers/${match.driverId}`);
        return;
      }

      setProfileModalMode('settings');
      setProfileModalOpen(true);
      showToast('error', 'No driver profile matched your linked Steam account yet.');
    } catch (err) {
      console.error('Unable to resolve your driver profile', err);
      showToast('error', 'Unable to open your profile right now.');
    }
  };

  const handleDiscordLogin = () => {
    const apiBase = getApiBaseUrl().replace(/\/api$/, '');
    window.location.assign(`${apiBase}/api/auth/discord/login`);
  };

  const handleSteamLink = () => {
    if (!authToken) return;
    const apiBase = getApiBaseUrl().replace(/\/api$/, '');
    window.location.assign(`${apiBase}/api/auth/steam/login?token=${encodeURIComponent(authToken)}`);
  };

  const handleSaveProfile = async (updates: Partial<UserProfile>) => {
    if (!authToken || !user) return;
    try {
      const res = await api.updateUserProfile(authToken, updates);
      const nextUser = res.data.user as UserProfile;
      persistAuthState(nextUser, authToken);
      setProfileModalOpen(false);
    } catch (err) {
      console.error('Unable to save profile', err);
    }
  };

  const handleLogout = () => {
    persistAuthState(null, null);
    setProfileModalOpen(false);
  };

  return (
    <div className={`app-shell ${isTouchDevice ? 'is-mobile' : ''}`}>
      <NavigationBar
        activePage={getActivePage()}
        theme={theme}
        logoUrl={theme === 'light' ? logoLightUrl ?? logoDarkUrl : logoDarkUrl ?? logoLightUrl}
        user={user}
        pageVisibility={pageVisibility}
        onToggleTheme={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
        onNavigate={(target) => {
          const routes: Record<string, string> = {
            home: '/',
            championships: '/championships',
            drivers: '/drivers',
            hotlap: '/hotlap',
          };
          navigate(routes[target] || '/');
        }}
        onStartDiscordLogin={handleDiscordLogin}
        onOpenProfilePage={handleOpenUserProfile}
        onOpenUserModal={handleOpenProfileModal}
        onLogout={handleLogout}
      />

      <UserProfileModal
        open={profileModalOpen}
        mode={profileModalMode}
        user={user}
        onClose={() => setProfileModalOpen(false)}
        onLogin={handleDiscordLogin}
        onSave={handleSaveProfile}
        onLinkSteam={handleSteamLink}
        onTimezoneChange={setDisplayTimezone}
      />

      {toast && (
        <div className="toast-stack" aria-live="polite">
          <div className={`toast ${toast.type}`}>
            <button className="toast-close" onClick={dismissToast} aria-label="Close notification">
              ×
            </button>
            <div>
              <div className="toast-title">{toast.type === 'success' ? 'Success' : 'Notice'}</div>
              <div>{toast.message}</div>
            </div>
          </div>
        </div>
      )}

      <main className="app-main">
        <Routes>
          <Route path="/" element={pageVisibility.home ? <HomePage broadcastPlatform={broadcastPlatform} broadcastChannel={broadcastChannel} broadcastChannelId={broadcastChannelId} displayTimezone={displayTimezone} steamId={user?.steamId} /> : <Navigate to="/championships" replace />} />
          <Route path="/championships" element={pageVisibility.championships ? <ChampionshipsPage broadcastPlatform={broadcastPlatform} broadcastChannel={broadcastChannel} displayTimezone={displayTimezone} /> : <Navigate to="/" replace />} />
          <Route path="/championships/:championshipId/:tab?" element={pageVisibility.championships ? <ChampionshipDetailsPageWrapper broadcastPlatform={broadcastPlatform} broadcastChannel={broadcastChannel} defaultRulebookType={defaultRulebookType} defaultRulebook={defaultRulebook} defaultRulebookPdfDataUrl={defaultRulebookPdfDataUrl} displayTimezone={displayTimezone} /> : <Navigate to="/" replace />} />
          <Route path="/drivers" element={pageVisibility.drivers ? <DriversPage /> : <Navigate to="/" replace />} />
          <Route path="/drivers/:driverId" element={pageVisibility.drivers ? <DriverDetailPageWrapper /> : <Navigate to="/" replace />} />
          <Route path="/hotlap" element={pageVisibility.hotlaps ? <HotlapPage /> : <Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// Wrapper components to handle URL params and data fetching
interface ChampionshipDetailsWrapperProps {
  broadcastPlatform: 'youtube' | 'twitch' | '';
  broadcastChannel: string;
  defaultRulebookType: 'text' | 'link' | 'pdf';
  defaultRulebook: string;
  defaultRulebookPdfDataUrl: string | null;
  displayTimezone: string;
}

const ChampionshipDetailsPageWrapper: React.FC<ChampionshipDetailsWrapperProps> = ({ broadcastPlatform, broadcastChannel, defaultRulebookType, defaultRulebook, defaultRulebookPdfDataUrl, displayTimezone }) => {
  const { championshipId, tab } = useParams<{ championshipId: string; tab?: string }>();
  const navigate = useNavigate();
  const [championship, setChampionship] = useState<Championship | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadChampionship = async () => {
      if (!championshipId) {
        navigate('/championships');
        return;
      }

      try {
        setLoading(true);
        const res = await api.getChampionships();
        const found = (res.data.championships || []).find((c: Championship) => c.id === championshipId);
        if (found) {
          setChampionship(found);
        } else {
          navigate('/championships');
        }
      } catch (err) {
        console.error('Failed to load championship:', err);
        navigate('/championships');
      } finally {
        setLoading(false);
      }
    };

    loadChampionship();
  }, [championshipId, navigate]);

  if (loading) {
    return <div className="empty-state">Loading championship...</div>;
  }

  if (!championship) {
    return null;
  }

  const validTabs = ['info', 'standings', 'races', 'drivers', 'practice'] as const;
  type ValidTab = typeof validTabs[number];
  const activeTab: ValidTab = validTabs.includes(tab as ValidTab) ? (tab as ValidTab) : 'races';

  const handleTabChange = (newTab: ValidTab) => {
    navigate(`/championships/${championshipId}/${newTab}`);
  };

  return <ChampionshipDetailsPage championship={championship} onBack={() => navigate('/championships')} activeTab={activeTab} onTabChange={handleTabChange} broadcastPlatform={broadcastPlatform} broadcastChannel={broadcastChannel} defaultRulebookType={defaultRulebookType} defaultRulebook={defaultRulebook} defaultRulebookPdfDataUrl={defaultRulebookPdfDataUrl} displayTimezone={displayTimezone} />;
};

const DriverDetailPageWrapper: React.FC = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const navigate = useNavigate();

  if (!driverId) {
    navigate('/drivers');
    return null;
  }

  return <DriverDetailPage driverId={driverId} onBack={() => navigate('/drivers')} />;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
